import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];
const REFRESH_MS = 30000;

export const isMissingCampaigns = (error) =>
  ['PGRST202', '42883', '42P01', 'PGRST205'].includes(error?.code) || /campaigns?|campaign_messages|create_campaign/i.test(error?.message || '');

const explain = (error, fallback) => {
  if (error?.code === '42501') return 'No tienes permiso sobre este negocio (solo el dueño o un administrador).';
  if (error?.code === '22023' && error?.message) return error.message.charAt(0).toUpperCase() + error.message.slice(1) + '.';
  return fallback;
};

// Campañas guardadas en el servidor: el envío lo hace n8n (un mensaje por minuto). Aquí solo se crean, se miran, se pausan o se cancelan.
export const useCampaigns = () => {
  const { activeBusinessId } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';
  const [campaigns, setCampaigns] = useState([]);
  const [messages, setMessages] = useState([]);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!activeBusinessId || isReadOnlyDemo) return;
    const c = await supabase.from('campaigns').select('id, name, status, total, created_at').eq('business_id', String(activeBusinessId)).order('created_at', { ascending: false }).limit(10);
    if (c.error) {
      const gone = isMissingCampaigns(c.error);
      setMissing(gone);
      if (!gone) toast.error(explain(c.error, 'No se pudieron leer las campañas.'));
      setCampaigns([]);
      setMessages([]);
      return;
    }
    setMissing(false);
    const list = c.data || [];
    setCampaigns(list);
    if (!list.length) { setMessages([]); return; }
    const m = await supabase.from('campaign_messages').select('campaign_id, status, customer_name, error').in('campaign_id', list.map((x) => x.id)).limit(2000);
    if (!m.error) setMessages(m.data || []);
  }, [activeBusinessId, isReadOnlyDemo]);

  useEffect(() => { load(); }, [load]);
  const running = campaigns.some((c) => c.status === 'activa');
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [running, load]);

  const guardDemo = () => {
    if (isReadOnlyDemo) { toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.'); return true; }
    return false;
  };

  // Devuelve {id, creados, omitidos} o null si no se pudo crear.
  const create = async ({ name, templateId, recipients }) => {
    if (guardDemo()) return null;
    setBusy(true);
    const { data, error } = await supabase.rpc('create_campaign', { p_business: String(activeBusinessId), p_name: name, p_template: templateId || null, p_recipients: recipients });
    setBusy(false);
    if (error) {
      if (isMissingCampaigns(error)) setMissing(true);
      toast.error(isMissingCampaigns(error) ? 'Falta ejecutar la migración 20261019a_campaigns.sql en Supabase.' : explain(error, 'No se pudo crear la campaña.'));
      return null;
    }
    await load();
    return data;
  };

  const setStatus = async (id, status) => {
    if (guardDemo()) return false;
    const { data, error } = await supabase.rpc('set_campaign_status', { p_id: id, p_status: status });
    if (error) { toast.error(explain(error, 'No se pudo cambiar la campaña.')); return false; }
    if (data === 'ya_cerrada') toast.error('Esa campaña ya está cerrada.');
    await load();
    return data === status;
  };

  return { campaigns, messages, missing, busy, isReadOnlyDemo, reload: load, create, setStatus };
};
